import type { RouteComponentProps } from 'react-router';
import { appendOrReplaceQueryString } from '@applied/utils/urls';
import { action, computed, makeObservable, observable } from 'mobx';
import * as qs from 'query-string';

type OnUnfocusCb = () => Promise<boolean> | boolean;

const URL_KEY = 'selectedTab';

export function getSelectedTabFromUrl(
  history: RouteComponentProps['history'],
  allLabels: string[],
): string {
  const params = qs.parse(history.location.search);
  const param = params[URL_KEY]?.toString();
  let selectedTab = param || allLabels[0];
  if (selectedTab && !allLabels.includes(selectedTab)) {
    selectedTab = allLabels[0];
  }
  return selectedTab;
}

export default class Tabs {
  @observable.deep private selectedTab!: /* Fixme: http://tiny.cc/r4g9vz */ string;
  private onUnfocusRegistry: Map<string, OnUnfocusCb>;
  private history: RouteComponentProps['history'];
  private linkInUrl: boolean;
  /**
   * Map of tab ids to the label to display for that tab.
   * Defaults to the tab names given in the constructor.
   */
  @observable.deep private tabToLabels: Map<string, string>;
  // Fires when the tab changes, but before the selectedTab is updated for any cleanup or side effect operations.
  private onTabChange: ((curTab: string, nextTab: string) => void) | undefined;

  /**
   * Constructor for the Tabs class.
   * @param allLabels Used to populate the tabToLabels map, dictating what labels to initially display for each tab. These labels will serve as the tab IDs.
   * @param history Instance of the history object from react-router. Used to determine the selected tab from the URL.
   * @param linkInUrl Whether or not to link the selected tab to the URL.
   * @param onTabChange Callback function for when the tab changes.
   */
  public constructor(
    allLabels: string[],
    history: RouteComponentProps['history'],
    linkInUrl = true,
    onTabChange?: ((curTab: string, nextTab: string) => void) | undefined,
  ) {
    // eslint-disable-next-line no-restricted-syntax
    makeObservable(this);
    if (linkInUrl && !history) {
      throw new Error('must provide history if linking in url');
    }
    this.onUnfocusRegistry = new Map();
    this.history = history;
    // TODO(EricH): Add a test for linkInUrl and then increase coverage thresholds again
    this.linkInUrl = linkInUrl;
    this.tabToLabels = new Map();
    for (const label of allLabels) {
      this.tabToLabels.set(label, label);
    }
    if (this.linkInUrl) {
      action(() => {
        this.selectedTab = getSelectedTabFromUrl(history, allLabels);
      })();
    } else {
      action(() => {
        this.selectedTab = allLabels[0];
      })();
    }
    this.onTabChange = onTabChange;
  }

  /**
   * Given a tab id, set the label to be displayed for that tab.
   * @param id: Unique identifier for the tab.
   * @param label: Label to display for the tab.
   */
  @action
  public setTabLabel(id: string, label: string): void {
    this.tabToLabels.set(id, label);
  }

  /**
   * Returns a list of all user-facing tab labels.
   */
  @computed
  public get allTabs(): readonly string[] {
    return Array.from(this.tabToLabels.values());
  }

  /**
   * Returns a list of all tab ids.
   */
  @computed
  public get allTabIds(): readonly string[] {
    return Array.from(this.tabToLabels.keys());
  }

  /**
   * Given a tab id, return the label to display for that tab.
   */
  public getTabLabel(id: string): string {
    return this.tabToLabels.get(id) || '';
  }

  /**
   * Returns the currently selected tab id.
   */
  @computed
  public get currentTabLabel(): string {
    return this.selectedTab;
  }

  public goToTab(label: string): void {
    action(() => {
      this.onTabChange?.(this.selectedTab, label);
      this.selectedTab = label;
    })();
    if (this.linkInUrl) {
      appendOrReplaceQueryString(URL_KEY, label, this.history);
    }
  }

  public registerOnUnfocusCb(label: string, cb: OnUnfocusCb): void {
    this.onUnfocusRegistry.set(label, cb);
  }

  public getOnUnfocusCb(label: string): OnUnfocusCb | undefined {
    if (!label) {
      return undefined;
    }

    return this.onUnfocusRegistry.get(label);
  }
}
